import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type SortingState } from '@tanstack/react-table'
import clsx from 'clsx'
import { AlertErrorCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { SortableHeaderCell } from '@/components/sortable-header/SortableHeaderCell'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'
import { DriveListItem } from '@/user_files/drive/components/drive-list-item/DriveListItem'
import { DriveEmptyState } from '@/user_files/drive/components/DriveEmptyState'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { UserFileProcessingBlockedReason, UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { FolderOverflowMenu } from '@/user_files/folders/components/FolderOverflowMenu'
import { FileOverflowMenu } from '@/user_files/files/components/FileOverflowMenu'
import { UserFileTypeIcon } from '@/user_files/files/components/UserFileTypeIcon'
import { downloadFile } from '@/user_files/files/fetch/download-user-file'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import { PROVIDER_COMPACT_DISPLAY_NAME } from '@/user_files/external-storage/ProviderMeta'
import { openInProviderTab, resolveOpenInProviderFile } from '@/user_files/external-storage/utils/open-in-provider'
import type { SyncedFileSelection } from '@/user_files/external-storage/hooks/useSyncedFileSelection'
import { fileProcessingStatusToDriveStatus, formatDriveListUpdatedAtParts, formatFileSize } from '@/user_files/utils/drive-list-format'
import type { FolderResponse } from '@/user_files/folders/schemas'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { EmailAttachmentDisclosure } from '@/user_files/components/EmailAttachmentDisclosure'
import { useEmailAttachmentExpansion } from '@/user_files/hooks/useEmailAttachmentExpansion'
import { filterVisibleGroupedFiles, groupExtractedFiles } from '@/user_files/utils/group-extracted-files'
import { InitialExternalSyncStatus } from './InitialExternalSyncStatus'

type SyncedRow =
  | { kind: 'folder'; data: FolderResponse }
  | {
      kind: 'file'
      data: UserFileRead
      attachmentDepth: number
      attachmentParentName?: string
      hasExtractedChildren: boolean
      extractedAttachmentCount: number
      attachmentsExpanded: boolean
    }

// Module-level — stable reference prevents table re-initialization on every render
const columnHelper = createColumnHelper<SyncedRow>()

type SyncedFileListProps = {
  blockingError?: string
  files: UserFileRead[]
  folders: FolderResponse[]
  loading: boolean
  initialSyncInProgress?: boolean
  provider: ExternalStorageProvider | null
  onNavigateToFolder: (folderId: string) => void
  onRetry?: () => void
  /** Replaces the default empty-folder copy. */
  emptyState?: ReactNode
  /** Omit to hide selection controls. */
  selection?: Pick<SyncedFileSelection, 'isFileSelected' | 'toggleFileSelect'>
  sorting?: SortingState
  /** Omit to disable sorting. */
  onSortingChange?: (sorting: SortingState) => void
}

/**
 * SyncedFileList
 *
 * Readonly table of a matter's synced DMS files, mirroring FolderList's table shell (TanStack
 * columns, sticky header, loading/error/empty states) but sourced from props instead of the
 * internal current-folder Redux state, since synced browsing is scoped to the mapping's subtree.
 */
export function SyncedFileList({
  folders,
  files,
  loading,
  initialSyncInProgress = false,
  blockingError,
  onRetry,
  onNavigateToFolder,
  provider,
  emptyState,
  selection,
  sorting,
  onSortingChange,
}: SyncedFileListProps) {
  const dispatch = useAppDispatch()
  const viewUserFile = useViewUserFile()
  const providerLabel = provider ? PROVIDER_COMPACT_DISPLAY_NAME[provider] : null
  const readOnlyNote = providerLabel ? `Read only. Managed by ${providerLabel}.` : ''

  const [isOverflowScrollActive, setIsOverflowScrollActive] = useState(false)
  const scrollingContainerRef = useRef<HTMLDivElement>(null)
  // Best-effort key: this list only receives files/folders as props (no currentFolderId), so empty
  // folders fall back to folders[0]?.parent_id or 'root'. MatterFilesPanel keys off the real folder id.
  const expansionResetKey = `${files[0]?.folder_id ?? folders[0]?.parent_id ?? 'root'}::${sorting?.[0]?.id ?? ''}::${sorting?.[0]?.desc ? 'desc' : 'asc'}`
  const { expandedIds, isExpanded, toggleExpanded } = useEmailAttachmentExpansion(expansionResetKey)
  const toggleAttachments = useCallback((fileId: string) => () => toggleExpanded(fileId), [toggleExpanded])

  useLayoutEffect(() => {
    const el = scrollingContainerRef.current
    if (!el) return
    const update = () => setIsOverflowScrollActive(el.scrollHeight > el.clientHeight)

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => ro.disconnect()
  }, [folders.length, files.length, loading])

  const rows = useMemo<SyncedRow[]>(() => {
    const grouped = groupExtractedFiles(files)
    return [
      ...folders.map((data) => ({ kind: 'folder' as const, data })),
      ...filterVisibleGroupedFiles(grouped, expandedIds).map(({ file, visualDepth, parentName, hasChildren, childCount }) => ({
        kind: 'file' as const,
        data: file,
        attachmentDepth: visualDepth,
        attachmentParentName: parentName,
        hasExtractedChildren: hasChildren,
        extractedAttachmentCount: childCount,
        attachmentsExpanded: hasChildren ? isExpanded(file.id) : false,
      })),
    ]
  }, [folders, files, expandedIds, isExpanded])

  const hasItems = rows.length > 0
  const showInitialSyncState = Boolean(initialSyncInProgress && providerLabel && !hasItems && !blockingError)
  const showLoadingState = loading && !blockingError && !showInitialSyncState
  const showErrorState = !!blockingError
  const shouldRenderDataRows = !showInitialSyncState && !showLoadingState && !showErrorState && hasItems

  const handleDownload = useCallback(
    async (file: UserFileRead) => {
      try {
        await downloadFile({ fileId: file.id, storagePath: file.storage_path, fileName: file.original_filename })
        dispatch(
          openGlobalToast({ type: GlobalToastType.SUCCESS, message: `${file.original_filename} downloaded successfully`, durationMs: kToastDurationShortMs })
        )
      } catch {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `${file.original_filename} download failed`, durationMs: kToastDurationMediumMs }))
      }
    },
    [dispatch]
  )

  const columns = useMemo(
    () => [
      // Matches DriveListItem's selection cell (`w-12 pl-4 pr-3 md:pl-2`). Left empty: with a
      // single-target action there is nothing for a select-all to do.
      columnHelper.display({ id: 'select', header: '', enableSorting: false, meta: { headerClassName: 'w-12 pl-4 pr-3 md:pl-2' } }),
      // `pl-0` is not redundant: browsers default `th` to 1px padding, and FolderList sets the same
      // class on its Name header. Without it this heading sits 1px right of the matter view's and
      // visibly nudges when the source toggle switches.
      columnHelper.display({
        id: 'name',
        header: 'Name',
        enableSorting: !!onSortingChange,
        meta: { headerClassName: 'pl-0 pr-6', sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' } },
      }),
      columnHelper.display({
        id: 'modified',
        header: 'Last Modified',
        enableSorting: !!onSortingChange,
        meta: {
          headerClassName: 'px-6 hidden w-[22%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_640px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort old to new', descendingLabel: 'Sort new to old' },
        },
      }),
      columnHelper.display({
        id: 'size',
        header: 'Size',
        enableSorting: !!onSortingChange,
        meta: {
          headerClassName: 'px-6 hidden w-[12%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap [@container_(min-width:_768px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort small to large', descendingLabel: 'Sort large to small' },
        },
      }),
      // No retry action in a readonly view, but DriveListItem always renders this slot — keep the
      // header column so header/row cell counts line up.
      columnHelper.display({ id: 'actions', header: '', meta: { headerClassName: 'w-12 px-2' } }),
      columnHelper.display({ id: 'overflow', header: '', meta: { headerClassName: 'w-10 pl-0 pr-2 md:pr-0' } }),
    ],
    [onSortingChange]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: sorting ? { sorting } : undefined,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => `${row.kind}-${row.data.id}`,
  })

  return (
    <div className="flex min-w-0 flex-col max-h-full mt-2 overflow-hidden [container-type:inline-size]">
      <div
        ref={scrollingContainerRef}
        className={clsx(
          // Keep the expand control under the pointer when attachment rows are removed below it.
          'relative overflow-y-auto [overflow-anchor:none]',
          isOverflowScrollActive && 'flex-1 min-h-0',
          !shouldRenderDataRows && 'min-h-36'
        )}
      >
        {/* Full-width status rows span hidden columns too; auto layout prevents those columns from stealing header space. */}
        <table className={clsx('w-full border-collapse', shouldRenderDataRows ? 'table-fixed' : 'table-auto')}>
          <caption className="sr-only">List of synced files and folders</caption>
          <thead className="bg-brand-neutral-50 sticky top-0 z-[1] text-sm text-brand-neutral-800 border-b [&>tr>th]:py-3 [&>tr>th]:text-left [&>tr>th]:font-medium">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // `getCanSort()` requires an accessor function, and every column here is a
                  // `display` column rendered from `row.original`, so the declared flag is read
                  // directly — the same reason `FolderList` does it this way.
                  if (onSortingChange && header.column.columnDef.enableSorting === true) {
                    return <SortableHeaderCell key={header.id} header={header} onSortingChange={onSortingChange} />
                  }

                  return (
                    <th key={header.id} className={header.column.columnDef.meta?.headerClassName}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody className={clsx('text-sm', shouldRenderDataRows && 'border-b border-brand-neutral-200')}>
            {showInitialSyncState && (
              <>
                <tr className="h-11 border-b border-brand-neutral-200">
                  {/* The status copy is a single `truncate` line, so under auto layout its full width
                      would set the table's, pushing the table past the container on a phone. Taking it
                      out of flow leaves the cell no width to contribute and lets it truncate against
                      the row it is painted over. */}
                  <td colSpan={columns.length} className="relative">
                    {/* `pl-12` matches the select column's `w-12`, so the spinner lines up with the
                        Name column rather than the table edge. */}
                    <div className="absolute inset-0 flex items-center pl-12 pr-2">
                      <InitialExternalSyncStatus sourceLabel={providerLabel ?? ''} description="Files will appear here as they finish importing." />
                    </div>
                  </td>
                </tr>
                {Array.from({ length: 10 }, (_, index) => (
                  <tr key={index} className="h-11 border-b border-brand-neutral-200" aria-hidden="true">
                    {/* Mirrors DriveListItem's selection cell so the skeleton lines up with real rows. */}
                    <td className="w-12 pl-4 pr-3 align-middle md:pl-2" />
                    <td className="pl-0 pr-6 align-middle">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <LoadingSkeleton width={56} lineHeight={12} />
                        <span className="min-w-0 max-w-[230px] flex-1">
                          <LoadingSkeleton width="100%" lineHeight={12} />
                        </span>
                      </div>
                    </td>
                    <td className="hidden px-6 align-middle [@container_(min-width:_640px)]:table-cell">
                      <LoadingSkeleton width={84} lineHeight={12} />
                    </td>
                    <td className="hidden px-6 align-middle [@container_(min-width:_768px)]:table-cell">
                      <LoadingSkeleton width={44} lineHeight={12} />
                    </td>
                    <td colSpan={2} className="px-2 align-middle">
                      <LoadingSkeleton width={64} lineHeight={12} />
                    </td>
                  </tr>
                ))}
              </>
            )}

            {showLoadingState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex items-center justify-center h-36">
                    <CircularProgressContinuousSized size={24} thickness={5} />
                  </div>
                </td>
              </tr>
            )}

            {showErrorState && (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex h-36 flex-col items-center justify-center gap-2 text-center">
                    <span className="flex items-center gap-1.5 text-sm leading-5 tracking-[-0.15px] text-true-gray-700">
                      <AlertErrorCircle className="size-3.5 shrink-0" aria-hidden />
                      {blockingError}
                    </span>
                    {onRetry && (
                      <Button type="button" variant="outlined" size="xs" onClick={onRetry}>
                        Retry
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )}

            {!showInitialSyncState && !showLoadingState && !showErrorState && !hasItems && (
              <tr>
                <td colSpan={columns.length}>{emptyState ?? <DriveEmptyState />}</td>
              </tr>
            )}

            {shouldRenderDataRows &&
              table.getRowModel().rows.map((row) => {
                const item = row.original
                if (item.kind === 'folder') {
                  const { date, time } = formatDriveListUpdatedAtParts(item.data.updated_at)
                  return (
                    <DriveListItem
                      key={row.id}
                      variant="folder"
                      // Folders have no selection action.
                      selectionPlaceholder
                      name={item.data.name}
                      formattedDate={date}
                      formattedTime={time}
                      onClick={() => onNavigateToFolder(item.data.id)}
                      overflowMenu={<FolderOverflowMenu />}
                    />
                  )
                }
                const { date, time } = formatDriveListUpdatedAtParts(item.data.updated_at)
                const openInProvider = resolveOpenInProviderFile(item.data.external_storage)
                const isTerminal =
                  item.data.processing_status === UserFileProcessingStatus.Completed || item.data.processing_status === UserFileProcessingStatus.Failed
                const isQueued = !isTerminal && item.data.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota
                const status = isQueued ? DriveListItemStatus.Queued : fileProcessingStatusToDriveStatus(item.data.processing_status)
                const isReady = status === DriveListItemStatus.Completed
                const errorMessage = status === DriveListItemStatus.Failed ? item.data.processing_error ?? 'Processing Failed' : undefined
                return (
                  <DriveListItem
                    key={row.id}
                    variant="file"
                    status={status}
                    selected={selection?.isFileSelected(item.data.id) ?? false}
                    onToggleSelect={selection ? () => selection.toggleFileSelect(item.data.id) : undefined}
                    selectionPlaceholder={!selection || (Boolean(item.data.extracted_from_file_id) && !item.data.external_storage)}
                    name={item.data.original_filename}
                    nameIndentLevel={item.attachmentDepth}
                    attachmentParentName={item.attachmentParentName}
                    attachmentCount={item.extractedAttachmentCount}
                    disclosure={
                      item.hasExtractedChildren ? (
                        <EmailAttachmentDisclosure
                          filename={item.data.original_filename}
                          expanded={item.attachmentsExpanded}
                          onToggle={toggleAttachments(item.data.id)}
                        />
                      ) : undefined
                    }
                    formattedDate={date}
                    formattedTime={time}
                    detailsLabel={item.data.size_bytes != null ? formatFileSize(item.data.size_bytes) : undefined}
                    errorMessage={errorMessage}
                    statusMessage={isQueued ? 'Waiting for OCR page quota' : undefined}
                    fileIcon={
                      <UserFileTypeIcon
                        detectedContentType={item.data.detected_content_type}
                        detectedExtension={item.data.detected_extension}
                        originalFilename={item.data.original_filename}
                        hasExtractedAttachments={item.hasExtractedChildren}
                        className="w-6 h-6 shrink-0 text-brand-neutral-950"
                      />
                    }
                    onClick={isReady ? () => viewUserFile(item.data) : undefined}
                    overflowMenu={
                      <FileOverflowMenu
                        onDownload={isReady ? () => handleDownload(item.data) : undefined}
                        externalProvider={openInProvider?.provider}
                        onOpen={openInProvider ? () => openInProviderTab(openInProvider.url) : undefined}
                        footerNote={readOnlyNote}
                        disabled={!isReady && !openInProvider}
                      />
                    }
                  />
                )
              })}
          </tbody>
        </table>
        <div className="h-32" />
      </div>
    </div>
  )
}
